/**
 * Helpers for normalizing .NET API JSON into portal DTOs.
 *
 * Gateway responses may use camelCase or PascalCase property names depending on
 * serializer settings. These pick the first value found under either key.
 */

/** Read a raw field from an API object (camelCase or PascalCase). */
export function pick<T>(
  record: Record<string, unknown>,
  camelKey: string,
  pascalKey: string
): T | undefined {
  const value = record[camelKey] ?? record[pascalKey];
  return value as T | undefined;
}

/** Non-empty trimmed string, or null when missing/blank. */
export function pickStr(
  record: Record<string, unknown>,
  camelKey: string,
  pascalKey: string
): string | null {
  const value = pick<string>(record, camelKey, pascalKey);
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
}

/** Finite number, or null when missing/non-numeric. */
export function pickNum(
  record: Record<string, unknown>,
  camelKey: string,
  pascalKey: string
): number | null {
  const value = pick<number | string>(record, camelKey, pascalKey);
  if (value == null || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

/** Non-empty ISO/date string, or null when missing/blank. */
export function pickDateStr(
  record: Record<string, unknown>,
  camelKey: string,
  pascalKey: string
): string | null {
  const value = pick<unknown>(record, camelKey, pascalKey);
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  return null;
}
